import React, { useEffect, useState } from 'react';
import { ref, onValue, remove, update } from 'firebase/database';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';

const HomeAdmin = () => {
  const navigate = useNavigate();
  const [adminName, setAdminName] = useState('');
  const [tours, setTours] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('tours');
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const storedName = localStorage.getItem('adminName');
    if (!storedName) {
      navigate('/Admin-home');
    } else {
      setAdminName(storedName);
    }

    // Load tours
    const toursRef = ref(db, 'tours');
    onValue(toursRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loaded = Object.entries(data).map(([id, value]) => ({
          id,
          ...value
        }));
        setTours(loaded.reverse());
      }
    });

    // Load messages
    const messagesRef = ref(db, 'messages');
    onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loaded = Object.entries(data).map(([id, value]) => ({
          id,
          ...value
        }));
        setMessages(loaded.reverse());
      }
    });
  }, [navigate]);

  const handleDeleteTour = (id) => {
    if (window.confirm("Are you sure you want to delete this tour?")) {
      remove(ref(db, `tours/${id}`))
        .then(() => alert("Tour deleted successfully."));
    }
  };

  const confirmDeleteMessage = (id) => {
    setMessageToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteMessage = () => {
    if (messageToDelete) {
      remove(ref(db, `messages/${messageToDelete}`))
        .then(() => {
          alert("Message deleted successfully.");
          setShowDeleteModal(false);
          setMessageToDelete(null);
        });
    }
  };

  const markAsRead = (id) => {
    update(ref(db, `messages/${id}`), { status: 'read' });
  };

  return (
    <div style={styles.container}>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirm Deletion</h3>
            <p>Are you sure you want to delete this message?</p>
            <div style={styles.modalButtons}>
              <button 
                style={styles.cancelButton}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button 
                style={styles.deleteButton}
                onClick={handleDeleteMessage}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.header}>
        <h2>🌐 Welcome, <span style={{ color: '#ffeb3b' }}>{adminName}</span></h2>
        <div style={styles.headerControls}>
          <div style={styles.tabContainer}>
            <button 
              style={activeTab === 'tours' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('tours')}
            >
              Tours
            </button>
            <button 
              style={activeTab === 'messages' ? styles.activeTab : styles.tab}
              onClick={() => setActiveTab('messages')}
            >
              Messages ({messages.filter(m => m.status === 'unread').length})
            </button>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem('adminName');
              navigate('/');
            }}
            style={styles.logoutBtn}
          >
            🔓 Logout
          </button>
        </div>
      </div>

      {activeTab === 'tours' ? (
        <>
          <TourSection
            title="🏞️ Domestic Tours"
            data={tours.filter(t => t.category === 'domestic-tour')}
            handleDelete={handleDeleteTour}
          />
          <TourSection
            title="✈️ International Tours"
            data={tours.filter(t => t.category === 'international-tour')}
            handleDelete={handleDeleteTour}
          />
        </>
      ) : (
        <MessageSection 
          messages={messages} 
          confirmDeleteMessage={confirmDeleteMessage}
          markAsRead={markAsRead}
        />
      )}
    </div>
  );
};

const TourSection = ({ title, data, handleDelete }) => {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.grid}>
        {data.length === 0 ? (
          <p>No tours found.</p>
        ) : (
          data.map(tour => (
            <div key={tour.id} style={styles.card}>
              <img src={tour.image} alt={tour.title} style={styles.cardImage} />
              <div style={styles.cardBody}>
                <h4>{tour.title}</h4>
                <p><strong>Duration:</strong> {tour.duration} days</p>
                <p>{tour.description}</p>
                <div style={styles.buttonGroup}>
                  <button style={styles.editBtn} onClick={() => alert('Edit logic can be added.')}>✏️ Edit</button>
                  <button style={styles.deleteBtn} onClick={() => handleDelete(tour.id)}>🗑️ Delete</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const MessageSection = ({ messages, confirmDeleteMessage, markAsRead }) => {
  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>📨 Contact Messages</h3>
      <div style={styles.tableContainer}>
        <table style={styles.messageTable}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Status</th>
              <th style={styles.tableHeader}>Name</th>
              <th style={styles.tableHeader}>Email</th>
              <th style={styles.tableHeader}>Phone</th>
              <th style={styles.tableHeader}>Message</th>
              <th style={styles.tableHeader}>Date</th>
              <th style={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 ? (
              <tr>
                <td colSpan="7" style={styles.noDataCell}>No messages found</td>
              </tr>
            ) : (
              messages.map(message => (
                <tr 
                  key={message.id} 
                  style={{
                    ...styles.tableRow,
                    backgroundColor: message.status === 'unread' ? '#f8f9fa' : 'white'
                  }}
                  onClick={() => markAsRead(message.id)}
                >
                  <td style={styles.tableCell}>
                    {message.status === 'unread' ? (
                      <span style={styles.unreadBadge}>NEW</span>
                    ) : (
                      <span style={styles.readBadge}>READ</span>
                    )}
                  </td>
                  <td style={styles.tableCell}>{message.name}</td>
                  <td style={styles.tableCell}>
                    <a href={`mailto:${message.email}`} style={styles.emailLink}>
                      {message.email}
                    </a>
                  </td>
                  <td style={styles.tableCell}>
                    <a href={`tel:${message.phone}`} style={styles.phoneLink}>
                      {message.phone}
                    </a>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.messagePreview}>
                      {message.message.length > 50 
                        ? `${message.message.substring(0, 50)}...` 
                        : message.message}
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    {new Date(message.timestamp).toLocaleDateString()}
                  </td>
                  <td style={styles.tableCell}>
                    <button 
                      style={styles.deleteTableBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteMessage(message.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '30px 20px',
    fontFamily: 'Segoe UI, sans-serif',
    background: 'linear-gradient(to right, #f1f9ff, #ffffff)',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    background: 'linear-gradient(to right, #1e3c72, #2a5298)',
    padding: '20px 30px',
    color: '#fff',
    borderRadius: '12px',
    boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
    marginBottom: '30px',
  },
  headerControls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px'
  },
  tabContainer: {
    display: 'flex',
    gap: '10px',
  },
  tab: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.3)',
    padding: '8px 16px',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  activeTab: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.5)',
    padding: '8px 16px',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  logoutBtn: {
    background: 'linear-gradient(to right, #ff416c, #ff4b2b)',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '30px',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  section: {
    marginBottom: '40px',
    overflowX: 'auto'
  },
  sectionTitle: {
    fontSize: '22px',
    color: '#1e88e5',
    marginBottom: '20px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px'
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column'
  },
  cardImage: {
    width: '100%',
    height: '180px',
    objectFit: 'cover'
  },
  cardBody: {
    padding: '15px'
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '15px'
  },
  editBtn: {
    backgroundColor: '#007bff',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  deleteBtn: {
    backgroundColor: '#dc3545',
    border: 'none',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  tableContainer: {
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  messageTable: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white'
  },
  tableHeader: {
    backgroundColor: '#1e3c72',
    color: 'white',
    padding: '12px 15px',
    textAlign: 'left',
    fontWeight: '600'
  },
  tableRow: {
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#f1f9ff !important'
    }
  },
  tableCell: {
    padding: '12px 15px',
    verticalAlign: 'top'
  },
  noDataCell: {
    padding: '20px',
    textAlign: 'center',
    color: '#7f8c8d'
  },
  unreadBadge: {
    background: '#3498db',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  readBadge: {
    background: '#95a5a6',
    color: 'white',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  emailLink: {
    color: '#3498db',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline'
    }
  },
  phoneLink: {
    color: '#2c3e50',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline'
    }
  },
  messagePreview: {
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  deleteTableBtn: {
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#c0392b'
    }
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '8px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalTitle: {
    marginTop: 0,
    color: '#2c3e50'
  },
  modalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },
  cancelButton: {
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#7f8c8d'
    }
  },
  deleteButton: {
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#c0392b'
    }
  },
  '@media (max-width: 768px)': {
    tableHeader: {
      padding: '8px 10px',
      fontSize: '14px'
    },
    tableCell: {
      padding: '8px 10px',
      fontSize: '14px'
    },
    messagePreview: {
      maxWidth: '150px'
    }
  },
  '@media (max-width: 576px)': {
    container: {
      padding: '20px 10px'
    },
    header: {
      padding: '15px 20px'
    },
    sectionTitle: {
      fontSize: '20px'
    },
    messageTable: {
      display: 'block',
      overflowX: 'auto'
    },
    messagePreview: {
      maxWidth: '100px'
    }
  }
};

export default HomeAdmin;